import React, { useState, useEffect, useCallback } from 'react';
import { MobileSheet } from '../../../ui/components/MobileSheet';
import { Button } from '../../../ui';
import { faLocationCrosshairs } from '@fortawesome/free-solid-svg-icons';
import { useRecommend, TransportMode } from '../hooks/useCoffeeShops';
import TransportPicker from './TransportPicker';
import ShopCard from './ShopCard';
import type { RecommendShop } from '../hooks/useCoffeeShops';
import type { CoffeeShop } from '../hooks/useCoffeeShops';

interface RecommendSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onShopClick?: (shopId: string) => void;
    coffeeTypesCache: Record<string, { iconName: string; displayName: string }>;
}

// map RecommendShop to CoffeeShop shape for ShopCard
function recommendToShopCard(rec: RecommendShop): CoffeeShop {
    return {
        _id: rec._id,
        name: rec.name,
        coordinates: rec.coordinates,
        address: rec.address,
        city: rec.city,
        country: rec.country,
        totalRatings: rec.totalRatings,
        averageRating: rec.averageRating,
        coffeeTypeStats: rec.coffeeTypeStats,
        googleRating: rec.googleRating ?? undefined,
        googleReviewCount: rec.googleReviewCount ?? undefined,
        types: []
    };
}

const RecommendSheet: React.FC<RecommendSheetProps> = ({
    open,
    onOpenChange,
    onShopClick,
    coffeeTypesCache
}) => {
    const [location, setLocation] = useState<{ lat: number; lng: number } | null>(null);
    const [locating, setLocating] = useState(false);
    const [locationError, setLocationError] = useState<string | null>(null);
    const [transport, setTransport] = useState<TransportMode>('foot');

    const {
        recommendations,
        unratedGems,
        clusterExists,
        loading,
        error,
        fetchRecommend
    } = useRecommend();

    const getLocation = useCallback(() => {
        setLocationError(null);
        setLocating(true);
        if (!navigator.geolocation) {
            setLocationError('Geolocation not supported');
            setLocating(false);
            return;
        }
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                setLocation({
                    lat: pos.coords.latitude,
                    lng: pos.coords.longitude
                });
                setLocating(false);
            },
            (err) => {
                setLocationError(err?.message || 'Failed to get location');
                setLocating(false);
            },
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        );
    }, []);

    // when sheet opens with no location, don't auto-request (user taps "Share location")
    // when location is set, fetch recommendations
    useEffect(() => {
        if (!open || !location) return;
        fetchRecommend(location.lat, location.lng, transport);
    }, [open, location?.lat, location?.lng, transport, fetchRecommend]);

    const handleShopClick = (shopId: string) => {
        onOpenChange(false);
        onShopClick?.(shopId);
    };

    return (
        <MobileSheet
            open={open}
            onOpenChange={onOpenChange}
            maxHeight="85vh"
            disableDrag={false}
            showGrabber={true}
            title="Recommend"
        >
            <div className="p-4 space-y-4">
                {!location && (
                    <div className="bg-sky/20 rounded-xl border-2 border-sky p-5">
                        <p className="text-center text-ink font-medium mb-3">
                            Share your location to see coffee shop picks near you
                        </p>
                        <Button
                            onClick={getLocation}
                            disabled={locating}
                            variant="default"
                            icon={faLocationCrosshairs}
                            className="w-full"
                        >
                            {locating ? 'Getting location...' : 'Allow Location Access'}
                        </Button>
                        {locationError && (
                            <p className="text-sm text-pink font-medium text-center mt-3">{locationError}</p>
                        )}
                    </div>
                )}

                {location && (
                    <>
                        <div className="bg-paper/80 rounded-xl border border-ink/15 p-3">
                            <p className="text-sm text-ink/80 text-center">
                                📍 {location.lat.toFixed(4)}, {location.lng.toFixed(4)}
                                <button
                                    type="button"
                                    onClick={getLocation}
                                    className="ml-2 text-sky underline hover:no-underline"
                                    disabled={locating}
                                >
                                    {locating ? 'updating...' : 'update'}
                                </button>
                            </p>
                            <div className="mt-2">
                                <p className="text-xs font-bold text-ink uppercase tracking-wide mb-2">
                                    How far can you go?
                                </p>
                                <TransportPicker value={transport} onChange={setTransport} />
                            </div>
                        </div>

                        {loading && (
                            <div className="flex flex-col items-center justify-center py-12">
                                <div className="w-10 h-10 border-4 border-muted border-t-sky rounded-full animate-spin mb-3" />
                                <p className="text-muted-foreground">Finding picks near you...</p>
                            </div>
                        )}

                        {error && !loading && (
                            <p className="text-sm text-pink font-medium text-center py-4">{error}</p>
                        )}

                        {!clusterExists && !loading && !error && location && (
                            <p className="text-center text-muted-foreground py-6">
                                No coffee cluster found for this area yet. Try another location or check back later.
                            </p>
                        )}

                        {clusterExists && !loading && !error && (
                            <div className="space-y-6">
                                {recommendations.length > 0 && (
                                    <section>
                                        <h3 className="text-sm font-bold text-ink uppercase tracking-wide mb-3">
                                            Top picks
                                        </h3>
                                        <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 scrollbar-thin">
                                            {recommendations.map((rec) => {
                                                const shop = recommendToShopCard(rec);
                                                return (
                                                    <div
                                                        key={rec._id}
                                                        className="flex-shrink-0 w-[280px]"
                                                    >
                                                        <ShopCard
                                                            shop={shop}
                                                            coffeeTypesCache={coffeeTypesCache}
                                                            distanceKm={rec.distanceKm}
                                                            adjustedGoogleRating={rec.adjustedGoogleRating}
                                                            onClick={() => handleShopClick(rec._id)}
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </section>
                                )}

                                {unratedGems.length > 0 && (
                                    <section>
                                        <h3 className="text-sm font-bold text-ink uppercase tracking-wide mb-1">
                                            Unrated but good
                                        </h3>
                                        <p className="text-xs text-muted-foreground mb-3">
                                            High Google rating, no community reviews yet — try them and rate
                                        </p>
                                        <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 scrollbar-thin">
                                            {unratedGems.map((rec) => {
                                                const shop = recommendToShopCard(rec);
                                                return (
                                                    <div
                                                        key={rec._id}
                                                        className="flex-shrink-0 w-[280px]"
                                                    >
                                                        <ShopCard
                                                            shop={shop}
                                                            coffeeTypesCache={coffeeTypesCache}
                                                            distanceKm={rec.distanceKm}
                                                            adjustedGoogleRating={rec.adjustedGoogleRating}
                                                            onClick={() => handleShopClick(rec._id)}
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </section>
                                )}

                                {recommendations.length === 0 && unratedGems.length === 0 && !loading && (
                                    <p className="text-center text-muted-foreground py-6">
                                        No shops in range for this transport mode. Try a larger range.
                                    </p>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
        </MobileSheet>
    );
};

export default RecommendSheet;
